/**
 * What the scene draws around the cup besides the cup itself: the Sim 9
 * gimme circle, the distance-scaled proximity rings of the lag modes, and
 * King of the Hill's gold "the king left it here" ring and gold cup.
 *
 * Pure data, no three.js: the app's PuttScene shell, the TV page, and the
 * app's tests all read the same rules from here.
 */
import { LAGKING_GOLD } from './brand';
import { CUP_RADIUS_FT } from './puttMath';

/** `colors.proximityGood` / `colors.proximityMid` in the app's theme tokens;
 *  the app's shared-package test asserts they stay equal. */
export const PROXIMITY_GOOD_COLOR = '#3FB950';
export const PROXIMITY_MID_COLOR = '#E3B341';

/** One reference ring centered on the cup. */
export interface PuttSceneCupRing {
  id: string;
  radiusFt: number;
  /** CSS hex color of the outline (and of the fill, when there is one). */
  color: string;
  /** Outline opacity; default 0.7. */
  opacity?: number;
  /** Opacity of the ring's band (from the next smaller ring, or the cup lip,
   *  out to this ring) while the ball is inside it. Like the gimme circle,
   *  the band fills while the rolling or settled ball is in it and empties
   *  if the ball rolls on through. Default 0.2. */
  fillOpacity?: number;
  /** Draw the outline as dashes instead of a solid line. An older host (or
   *  TV page) ignores it and draws a solid ring. */
  dashed?: boolean;
  /** Small crowns evenly spaced along the outline, in the ring's color
   *  (King of the Hill's crown line). Ignored by an older host. */
  crowns?: boolean;
}

/**
 * A second cup drawn on the green at its own distance, with an optional ring
 * around it (Baseball's four holes in a line and its bunt cup, 2026-10-08).
 * Decoration only: the scene judges nothing against it and the ball never
 * drops into it; the host owns what a ball near it means. Drawn whether or
 * not the scene's own cup is hidden. An older host or TV page ignores it.
 */
export interface PuttSceneExtraCup {
  id: string;
  /** Distance down the putt line from the ball, in feet. */
  distanceFt: number;
  /** Ring radius around this cup; omit for a bare cup. */
  ringRadiusFt?: number;
  /** CSS hex color of the ring. */
  color?: string;
  /** Ring outline opacity; default 0.7. */
  opacity?: number;
  /** Fill the ring (a ball stopped in it, or the hole in play). */
  lit?: boolean;
  /** Draw the ring as dashes (Baseball's sacrifice-fly zone). An older host
   *  or TV draws it solid. */
  dashed?: boolean;
  /** A ring only: no cup mouth (a second ring around a cup drawn by another entry). */
  ringOnly?: boolean;
  /** Football transitions (2026-10-17): drawn but faded out. With `fadeMs`
   *  the ring fades to and from hidden; an older host or TV ignores both. */
  hidden?: boolean;
  /** A flagstick stands in this cup (Football's answered cup, Cole
   *  2026-10-17). The scene's own flag stays where the target is, so the
   *  camera framing does not move. An older host or TV ignores it. */
  flag?: boolean;
  /** The flag's cloth and stick color (default the gold cloth on an
   *  off-white stick). Football's answered cup is white, so it reads against
   *  the yellow goalposts (Cole, 2026-10-17). An older receiver ignores it. */
  flagColor?: string;
  /** 'field': Football's cup on painted turf (Cole, 2026-10-18: the cups
   *  read as flickering slivers on the TV): a solid dark mouth and a white
   *  rim drawn over the turf with a depth offset, so it is a clean round hole
   *  at any distance. Omitted: the golf cup. An older receiver ignores it. */
  holeStyle?: 'field';
  /** How long a full fade in or out takes (ms); omit to switch at once. */
  fadeMs?: number;
}

/** The smallest radius a ring is drawn at, so a ring is never lost inside or
 *  on the cup's own lip (a 2 in leave still reads as a ring around the cup). */
export const MIN_CUP_RING_RADIUS_FT = CUP_RADIUS_FT * 1.6;

/** The drawn radius for a ring: at least MIN_CUP_RING_RADIUS_FT. */
export function drawnCupRingRadiusFt(radiusFt: number): number {
  return Math.max(MIN_CUP_RING_RADIUS_FT, radiusFt);
}

/**
 * The Sim 9 gimme circle (5 ft since 2026-10-19, 3 ft before) belongs to
 * Sim 9 only (Cole, 2026-09-29: "the 3' gimme circle should only exist on
 * Sim 9"). A host draws it when it asks
 * (`showGimme`, the Sim 9 screens) or when it sets its own scoring radius
 * (`gimmeRadiusFt`: HORSE's distance-scaled window, Poker's card zones, where
 * the ring IS the rule). `hideGimme` always wins.
 */
export function gimmeCircleVisible(input: {
  showGimme?: boolean | null;
  hideGimme?: boolean | null;
  gimmeRadiusFt?: number | null;
}): boolean {
  if (input.hideGimme) return false;
  if (input.showGimme) return true;
  return input.gimmeRadiusFt != null && input.gimmeRadiusFt > 0;
}

/**
 * The lag modes' two reference rings: a small green ring at the "good" band
 * edge and a larger yellow ring at the "mid" band edge, both scaled to the
 * putt's distance by the same thresholds that color the proximity readout
 * (the app's `getProximityBands`), so a leave inside the green ring is the
 * leave the HUD calls green.
 */
export function proximityBandCupRings(bands: {
  goodMaxFt: number;
  midMaxFt: number;
}): PuttSceneCupRing[] {
  const rings: PuttSceneCupRing[] = [];
  if (Number.isFinite(bands.goodMaxFt) && bands.goodMaxFt > 0) {
    rings.push({ id: 'band-good', radiusFt: bands.goodMaxFt, color: PROXIMITY_GOOD_COLOR, opacity: 0.95, fillOpacity: 0.22 });
  }
  if (Number.isFinite(bands.midMaxFt) && bands.midMaxFt > bands.goodMaxFt) {
    rings.push({ id: 'band-mid', radiusFt: bands.midMaxFt, color: PROXIMITY_MID_COLOR, opacity: 0.95, fillOpacity: 0.16 });
  }
  return rings;
}

/** Why a crown line is or is not drawn (see `kothCrownLine`). */
export type KothCrownLine =
  | { kind: 'none' }
  /** Hole it: the leave that still wins is inside the cup's own lip. */
  | { kind: 'hole-out'; requiredFt: number }
  /** A leave strictly inside `requiredFt` takes (or raises) the crown. */
  | { kind: 'ring'; requiredFt: number };

/**
 * King of the Hill's crown line (Cole, 2026-09-29): on the LAST putt, the
 * leave you need to beat the king's total, drawn as a dashed gold circle
 * with crowns on it. `requiredFt` = the king's total minus your total over
 * the first four holes.
 *
 * Tie rule: the backend (`decideKothCrown`) takes the crown only on a
 * STRICTLY lower score; an equal total is "not better". So a leave exactly
 * on the line loses, and the circle marks strictly inside.
 *
 * - No king yet, not the final putt, or earlier holes missing: nothing.
 * - Nothing left to beat (the king's total is already at or under yours): nothing.
 * - The winning leave is too small to draw as a ring outside the cup lip
 *   (under MIN_CUP_RING_RADIUS_FT): 'hole-out', shown as the gold cup liner.
 */
export function kothCrownLine(input: {
  kingTotalFt: number | null | undefined;
  /** Leaves of the holes already played, in order (holed = 0). */
  priorLeavesFt: ReadonlyArray<number>;
  holeCount: number;
  /** The hole the green shows, 0-based. */
  puttIndex: number;
}): KothCrownLine {
  const { kingTotalFt, priorLeavesFt, holeCount, puttIndex } = input;
  if (kingTotalFt == null || !Number.isFinite(kingTotalFt) || kingTotalFt < 0) return { kind: 'none' };
  if (holeCount < 1 || puttIndex !== holeCount - 1) return { kind: 'none' };
  if (priorLeavesFt.length < puttIndex) return { kind: 'none' };
  let sofar = 0;
  for (let i = 0; i < puttIndex; i++) {
    const leave = priorLeavesFt[i];
    if (!Number.isFinite(leave) || leave < 0) return { kind: 'none' };
    sofar += leave;
  }
  const requiredFt = kingTotalFt - sofar;
  // Strictly lower wins, so a holed putt (leave 0) wins only while required > 0.
  if (!(requiredFt > 1e-9)) return { kind: 'none' };
  if (requiredFt < MIN_CUP_RING_RADIUS_FT) return { kind: 'hole-out', requiredFt };
  return { kind: 'ring', requiredFt };
}

/** The crown line as a cup ring: dashed gold with crowns, filling like the
 *  king's own ring while the ball is inside it. */
export function crownLineCupRing(requiredFt: number): PuttSceneCupRing {
  return {
    id: 'koth-crown-line',
    radiusFt: requiredFt,
    color: LAGKING_GOLD,
    opacity: 0.95,
    fillOpacity: 0.2,
    dashed: true,
    crowns: true,
  };
}

/** How many crowns sit on a crown line of this radius: about one every 3 ft
 *  of circumference, between 6 and 8, so a small ring is not a crown pileup
 *  and a big one still reads as a crown line. */
export function crownCountForRadius(radiusFt: number): number {
  if (!Number.isFinite(radiusFt) || radiusFt <= 0) return 0;
  return Math.max(6, Math.min(8, Math.round((2 * Math.PI * radiusFt) / 3)));
}

export interface KingCupMarkers {
  cupRings: PuttSceneCupRing[];
  /** Gold when the king holed this putt; null keeps the white liner. */
  cupLinerColor: string | null;
}

/**
 * King of the Hill: one gold ring at the king's leave on the same putt
 * number, or a gold cup when the king holed it (a holed putt's leave is 0;
 * see `entryHoleLeaves` in the backend). No king data for the putt, nothing.
 * The ring fills while your ball is inside it, like every cup ring.
 */
export function kingCupMarkers(
  kingHoleLeaves: ReadonlyArray<number | null> | null | undefined,
  puttIndex: number,
): KingCupMarkers {
  const leave = kingHoleLeaves?.[puttIndex];
  if (leave == null || !Number.isFinite(leave) || leave < 0) {
    return { cupRings: [], cupLinerColor: null };
  }
  if (leave === 0) return { cupRings: [], cupLinerColor: LAGKING_GOLD };
  return {
    cupRings: [{ id: 'king-leave', radiusFt: leave, color: LAGKING_GOLD, opacity: 0.95, fillOpacity: 0.24 }],
    cupLinerColor: null,
  };
}

/**
 * Ring stroke width: thick enough to read on a phone in the holder at foot
 * level (Android tester, 2026-09-30: "a darker or more prominent circle would
 * look better on a small screen"), scaled with the ring so a 1 ft ring stays
 * a line and a 9 ft ring doesn't turn hairline.
 */
export function cupRingLineFt(radiusFt: number): number {
  return Math.min(0.15, Math.max(0.07, radiusFt * 0.04), radiusFt * 0.25);
}

/** A thin dark edge just outside every ring so it stands off the turf. */
export const CUP_RING_OUTLINE_COLOR = '#0B1729';
export const CUP_RING_OUTLINE_OPACITY = 0.45;
export function cupRingOutlineFt(lineFt: number): number {
  return lineFt * 0.6;
}

/** Extra cups (Baseball) the ground punches a real hole for. Past this many
 *  a cup falls back to a flat mouth decal (Baseball draws at most six). */
export const MAX_EXTRA_CUP_HOLES = 8;
/** An unused hole slot: far behind the address, never on the drawn ground. */
export const NO_EXTRA_CUP_HOLE_Z = 1e5;

/**
 * Which extra cups get a real recessed cup: the first cup mouth at each
 * distance (a second entry at the same spot, Baseball's leave ring, is drawn
 * by the first), ring-only entries excluded, at most MAX_EXTRA_CUP_HOLES.
 * Returns the owning cup ids and the ground-shader slot values (course z).
 */
export function extraCupHoles(
  cups: readonly PuttSceneExtraCup[],
): { ids: Set<string>; covered: Set<string>; z: Float32Array } {
  const ids = new Set<string>();
  const covered = new Set<string>();
  const z = new Float32Array(MAX_EXTRA_CUP_HOLES).fill(NO_EXTRA_CUP_HOLE_Z);
  const seen: number[] = [];
  for (const cup of cups) {
    if (cup.ringOnly || !(cup.distanceFt > 0)) continue;
    if (seen.some((d) => Math.abs(d - cup.distanceFt) < 1e-6)) {
      covered.add(cup.id);
      continue;
    }
    if (seen.length >= MAX_EXTRA_CUP_HOLES) continue;
    z[seen.length] = -cup.distanceFt;
    seen.push(cup.distanceFt);
    ids.add(cup.id);
  }
  return { ids, covered, z };
}
